import React, { useState } from "react";

// 1. DATA-DRIVEN SCENE CONFIGURATION
const SCENE = {
  cast: {
    speaker: "Alex", 
    listener: "You",
  },
  context: "Alex cleaned the house all day. You started dishes at 7:10pm.",
  lines: {
    trigger: "I see you finally decided to do the dishes. It's about time.",
    defaultReaction: "Why do you always have to be so critical?",
    wiseResponse: "I hear you — you've been carrying a lot today and want it noticed. I'm doing the dishes now, and I want us to figure out a fair split so it doesn't build up."
  },
  diagnostics: {
    event: "Dishes were not done until evening.",
    story: "Alex thinks I'm lazy / I am being attacked.",
    coreNeed: "Alex wants their earlier effort recognized & chores shared."
  },
  // THE NEW COACHING MODULE
  upgrade: {
    patternName: "Need → Attack Wrapper → Defense → Disconnection",
    coreTruth: "You aren't fighting about dishes. You're fighting about meaning: fairness, appreciation, and whether someone is carrying the relationship alone.",
    speaker1on1: {
      hardTruth: "Sarcasm is a relationship tax. It costs you connection every time you use it.",
      newScript: "I'm feeling overwhelmed and unappreciated. I need us to share the load without me having to push."
    },
    listener1on1: {
      hardTruth: "When they speak with contempt, your nervous system reads it as a threat. Your job is to not take the bait.",
      newScript: "That landed as a dig. I'm not going to fight. What do you actually need right now?"
    },
    agreement: {
      system: "Daily 'Kitchen Close' at 6:45pm.",
      appreciation: "One specific 'Thank You' per day."
    }
  }
};

export default function TheActorGapCard() {
  const [step, setStep] = useState(0);
  const [activeTab, setActiveTab] = useState("summary"); // For Step 5 navigation

  const nextStep = () => setStep((s) => s + 1);
  const reset = () => {
    setStep(0);
    setActiveTab("summary");
  };

  // HUD STYLING
  const cardBg = "#1E293B";
  const textColor = "#F1F5F9";
  const mutedColor = "#94A3B8";
  const borderColor = "#475569";
  const accentColor = "#F59E0B"; // Gold for the "Coach"

  return (
    <section
      className="card lg section"
      style={{
        position: "relative",
        background: cardBg,
        color: textColor,
        border: `1px solid ${borderColor}`,
        overflow: "hidden",
        padding: 0, 
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)"
      }}
    >
      {/* HUD HEADER */}
      <div
        style={{
          background: "rgba(0,0,0,0.3)",
          padding: "12px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${borderColor}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: step === 5 ? accentColor : step === 4 ? "#22C55E" : step === 0 ? "#EF4444" : "#3B82F6",
              boxShadow: step === 5 ? `0 0 8px ${accentColor}` : "none",
            }}
          />
          <span
            className="xs"
            style={{
              fontFamily: "monospace",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              color: mutedColor,
              fontSize: 11
            }}
          >
            SIMULATION // {
              step === 0 ? "CONTACT" : 
              step === 1 ? "INTERNAL DIAGNOSTICS" : 
              step === 2 ? "EXTERNAL FORENSICS" : 
              step === 3 ? "SYNTHESIS" : 
              step === 4 ? "COMPLETE" : 
              "SYSTEM UPGRADE"
            }
          </span>
        </div>
        {step > 0 && (
          <button
            onClick={reset}
            className="xs"
            style={{
              background: "transparent",
              border: `1px solid ${borderColor}`,
              color: mutedColor,
              padding: "2px 8px",
              borderRadius: 4,
              cursor: "pointer",
              fontFamily: "monospace",
            }}
          >
            RESET
          </button>
        )}
      </div>

      {/* CAST BAR */}
      <div style={{ background: "#0F172A", padding: "8px 20px", borderBottom: `1px solid ${borderColor}`, display: 'flex', gap: 24 }}>
        <div className="xs" style={{ color: mutedColor }}>
          SPEAKER: <span style={{ color: "#FCA5A5", fontWeight: 700 }}>{SCENE.cast.speaker}</span>
        </div>
        <div className="xs" style={{ color: mutedColor }}>
          LISTENER: <span style={{ color: "#93C5FD", fontWeight: 700 }}>{SCENE.cast.listener} (You)</span>
        </div>
      </div>

      {/* THE STAGE */}
      <div style={{ padding: "32px 24px", minHeight: 460, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        
        {/* STEP 0: CONTACT */}
        {step === 0 && (
          <div className="animate-in" style={{ textAlign: "center" }}>
            <div style={{ marginBottom: 32, textAlign: 'left', background: '#334155', borderRadius: '12px 12px 12px 0', padding: 20, maxWidth: '90%', margin: '0 auto 32px' }}>
               <div className="xs" style={{ color: "#FCA5A5", fontWeight: 700, marginBottom: 8 }}>
                 {SCENE.cast.speaker} SAYS:
               </div>
               <div style={{ fontFamily: 'serif', fontSize: '1.3rem', fontStyle: 'italic', color: '#fff' }}>
                 "{SCENE.lines.trigger}"
               </div>
            </div>
            <div style={{ borderTop: `1px dashed ${borderColor}`, paddingTop: 24, width: '100%' }}>
               <div className="xs" style={{ color: "#93C5FD", fontWeight: 700, marginBottom: 8 }}>
                 {SCENE.cast.listener} HEAR:
               </div>
               <div className="xs" style={{ color: "#EF4444", fontWeight: 700, letterSpacing: '0.1em', background: 'rgba(239, 68, 68, 0.1)', display: 'inline-block', padding: '6px 12px', borderRadius: 4, border: '1px solid #EF4444' }}>
                 ⚠ ATTACK DETECTED
               </div>
            </div>
            <button
              onClick={nextStep}
              style={{
                marginTop: 32,
                background: "#EF4444",
                color: "white",
                border: "none",
                padding: "16px 32px",
                fontSize: "1rem",
                fontWeight: 700,
                borderRadius: 50,
                cursor: "pointer",
                boxShadow: "0 0 25px rgba(239, 68, 68, 0.4)",
              }}
            >
              ⏸ PAUSE REACTION
            </button>
          </div>
        )}

        {/* STEP 1: INTERNAL DIAGNOSTICS */}
        {step === 1 && (
          <div className="animate-in">
            <h3 className="h3" style={{ color: textColor, marginTop: 0, textAlign: 'center', marginBottom: 8 }}>
              Internal Diagnostics
            </h3>
            <p className="xs" style={{ color: mutedColor, textAlign: 'center', marginBottom: 24 }}>
              Reading systems for <strong>{SCENE.cast.listener}</strong>...
            </p>
            <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 8, padding: 16, border: "1px dashed #F59E0B", marginBottom: 24 }}>
              <div className="xs" style={{ color: "#F59E0B", fontWeight: 700, marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
                <span>SYSTEM_LOGS</span>
                <span>STATUS: REACTIVE</span>
              </div>
              <ul className="xs" style={{ color: "#FCA5A5", fontFamily: 'monospace', listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{marginBottom: 6}}>{`> YOU_INTERPRETATION: "${SCENE.diagnostics.story}"`}</li>
                <li style={{marginBottom: 6}}>{`> YOU_SENSATION: Chest tightness.`}</li>
                <li style={{color: '#EF4444'}}>{`> YOU_URGE: Defend & Counter-attack.`}</li>
              </ul>
            </div>
            <div style={{ textAlign: "center", marginTop: 24 }}>
              <button
                onClick={nextStep}
                className="pill"
                style={{
                  background: "#F59E0B",
                  color: "#451A03",
                  borderColor: "#F59E0B",
                  fontWeight: 600
                }}
              >
                Systems Stabilized. Proceed →
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: EXTERNAL FORENSICS */}
        {step === 2 && (
          <div className="animate-in">
            <h3 className="h3" style={{ color: textColor, marginTop: 0, textAlign: 'center' }}>
              Scene Analysis
            </h3>
            <div style={{ background: "rgba(59, 130, 246, 0.1)", border: "1px solid #3B82F6", padding: 12, borderRadius: 8, marginBottom: 24 }}>
              <div className="xs" style={{ color: "#93C5FD", fontWeight: 700, marginBottom: 4 }}>
                WHAT IS ACTUALLY HAPPENING:
              </div>
              <div className="sm" style={{ color: "#BFDBFE" }}>
                <strong>Event:</strong> {SCENE.diagnostics.event}<br/>
                <strong>Reality:</strong> {SCENE.context} <br/>
                <strong>Dynamic:</strong> Alex is frustrated and using sarcasm. You are hearing it as an attack.
              </div>
            </div>
            <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div style={{ background: "rgba(239, 68, 68, 0.05)", border: "1px solid rgba(239, 68, 68, 0.4)", padding: 16, borderRadius: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 20 }}>🥓</span>
                  <span className="xs" style={{ color: "#FCA5A5", fontWeight: 700 }}>WRAPPER</span>
                </div>
                <div className="xs" style={{ color: "#FECACA" }}>
                  <strong>Source:</strong> {SCENE.cast.speaker}<br/>
                  <strong>Tone:</strong> Snarky.<br/>
                  <strong>Action:</strong> IGNORE
                </div>
              </div>
              <div style={{ background: "rgba(59, 130, 246, 0.1)", border: "1px solid rgba(59, 130, 246, 0.5)", padding: 16, borderRadius: 12, boxShadow: "0 0 20px rgba(59, 130, 246, 0.1)" }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 20 }}>🐚</span>
                  <span className="xs" style={{ color: "#93C5FD", fontWeight: 700 }}>CORE NEED</span>
                </div>
                <div className="xs" style={{ color: "#BFDBFE" }}>
                  <strong>Source:</strong> {SCENE.cast.speaker}<br/>
                  <strong>Truth:</strong> "{SCENE.diagnostics.coreNeed}"
                </div>
              </div>
            </div>
            <div style={{ textAlign: "center", marginTop: 24 }}>
              <button
                onClick={nextStep}
                className="pill"
                style={{
                  background: "transparent",
                  color: "#93C5FD",
                  borderColor: "#3B82F6",
                  borderWidth: 1,
                }}
              >
                Formulate Response →
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: REWRITE */}
        {step === 3 && (
          <div className="animate-in" style={{ textAlign: "center" }}>
             <h3 className="h3" style={{ color: textColor, marginTop: 0 }}>
              Script Editor
            </h3>
            <p className="sm" style={{ color: mutedColor }}>
              Rewrite <strong>{SCENE.cast.listener}'s</strong> line. Address the <strong>need</strong>, not the <strong>snark</strong>.
            </p>
            <div style={{ margin: "32px 0", textAlign: "left" }}>
               <div style={{ opacity: 0.4, marginBottom: 16, fontFamily: 'monospace' }} className="sm">
                <span className="xs" style={{color: '#EF4444', display: 'block', marginBottom: 4}}>YOU (DEFAULT REACTION):</span> 
                "{SCENE.lines.defaultReaction}"
              </div>
              <div style={{ background: "rgba(34, 197, 94, 0.1)", borderLeft: "4px solid #22C55E", padding: 16, borderRadius: "0 8px 8px 0", color: "#86EFAC", fontFamily: 'monospace', fontSize: '1.0rem', boxShadow: "0 4px 20px rgba(34, 197, 94, 0.1)" }}>
                <span className="xs" style={{color: '#22C55E', fontWeight: 700, display: 'block', marginBottom: 4}}>YOU (DIRECTOR'S CUT):</span> 
                "{SCENE.lines.wiseResponse}"
              </div>
            </div>
            <button
              onClick={nextStep}
              className="pill"
              style={{
                background: "#22C55E",
                color: "#022C22",
                borderColor: "#22C55E",
                fontWeight: 700
              }}
            >
              Action! (Run Scene)
            </button>
          </div>
        )}

        {/* STEP 4: RESOLUTION */}
        {step === 4 && (
          <div className="animate-in" style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✨</div>
            <h3 className="h3" style={{ color: textColor, marginTop: 0 }}>
              Scene Clear
            </h3>
            <p className="sm" style={{ color: mutedColor, maxWidth: 400, margin: "0 auto 24px" }}>
              You validated {SCENE.cast.speaker}'s effort, committed to doing the dishes, and proposed a system fix. <br/><br/>
              The "Bacon" was ignored. The "Scallop" was fed.
            </p>
            <button
              onClick={nextStep}
              className="pill"
              style={{
                background: "transparent",
                color: accentColor,
                borderColor: accentColor,
                borderWidth: 1,
              }}
            >
              Unlock System Upgrade →
            </button>
          </div>
        )}

        {/* STEP 5: THE COACH'S UPGRADE (New Module) */}
        {step === 5 && (
          <div className="animate-in">
            {/* 5a. Header */}
            <h3 className="h3" style={{ color: accentColor, marginTop: 0, textAlign: 'center', marginBottom: 4 }}>
              System Upgrade: The Repair Protocol
            </h3>
            <p className="xs" style={{ color: mutedColor, textAlign: 'center', marginBottom: 20 }}>
              Coach's Analysis & Future Prevention
            </p>

            {/* 5b. The Navigation Tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: `1px solid ${borderColor}`, paddingBottom: 16, overflowX: 'auto' }}>
              {[
                { id: "summary", label: "THE PATTERN" },
                { id: "alex", label: "1:1 WITH ALEX" },
                { id: "you", label: "1:1 WITH YOU" },
                { id: "plan", label: "7-DAY PLAN" }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    background: activeTab === tab.id ? "rgba(255,255,255,0.1)" : "transparent",
                    color: activeTab === tab.id ? "#fff" : mutedColor,
                    border: "none",
                    borderRadius: 4,
                    padding: "6px 12px",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    cursor: "pointer",
                    whiteSpace: 'nowrap'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* 5c. Tab Content Areas */}
            <div style={{ minHeight: 180 }}>
              
              {/* TAB 1: SUMMARY */}
              {activeTab === "summary" && (
                <div className="animate-in">
                  <div style={{ background: `rgba(245, 158, 11, 0.1)`, borderLeft: `4px solid ${accentColor}`, padding: 16, borderRadius: "0 8px 8px 0", marginBottom: 16 }}>
                    <div className="xs" style={{ color: accentColor, fontWeight: 700, marginBottom: 8 }}>DIAGNOSIS</div>
                    <p className="sm" style={{ color: "#E2E8F0", fontStyle: "italic", margin: 0 }}>
                      "{SCENE.upgrade.coreTruth}"
                    </p>
                  </div>
                  <div style={{ padding: "0 8px" }}>
                    <div className="xs" style={{ color: mutedColor, marginBottom: 8 }}>THE LOOP WE ARE BREAKING:</div>
                    <div className="sm" style={{ color: "#fff", fontFamily: "monospace" }}>
                      {SCENE.upgrade.patternName}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: ALEX 1:1 */}
              {activeTab === "alex" && (
                <div className="animate-in">
                  <div className="xs" style={{ color: "#FCA5A5", fontWeight: 700, marginBottom: 12 }}>COACH TO ALEX (SPEAKER):</div>
                  <p className="sm" style={{ color: "#E2E8F0", marginBottom: 24 }}>
                    "{SCENE.upgrade.speaker1on1.hardTruth}"
                  </p>
                  
                  <div style={{ background: "#334155", padding: 16, borderRadius: 8 }}>
                    <div className="xs" style={{ color: mutedColor, marginBottom: 8 }}>NEW SCRIPT (CLEAN SIGNAL):</div>
                    <div className="sm" style={{ color: "#FCA5A5", fontFamily: "monospace" }}>
                      "{SCENE.upgrade.speaker1on1.newScript}"
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 3: YOU 1:1 */}
              {activeTab === "you" && (
                <div className="animate-in">
                  <div className="xs" style={{ color: "#93C5FD", fontWeight: 700, marginBottom: 12 }}>COACH TO YOU (LISTENER):</div>
                  <p className="sm" style={{ color: "#E2E8F0", marginBottom: 24 }}>
                    "{SCENE.upgrade.listener1on1.hardTruth}"
                  </p>
                  
                  <div style={{ background: "#334155", padding: 16, borderRadius: 8 }}>
                    <div className="xs" style={{ color: mutedColor, marginBottom: 8 }}>NEW SCRIPT (META-RESPONSE):</div>
                    <div className="sm" style={{ color: "#93C5FD", fontFamily: "monospace" }}>
                      "{SCENE.upgrade.listener1on1.newScript}"
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 4: THE PLAN */}
              {activeTab === "plan" && (
                <div className="animate-in">
                  <div className="xs" style={{ color: "#22C55E", fontWeight: 700, marginBottom: 12 }}>THE 7-DAY EXPERIMENT</div>
                  
                  <ul className="sm" style={{ color: "#E2E8F0", paddingLeft: 20, marginBottom: 24 }}>
                    <li style={{marginBottom: 8}}><strong>No Sarcasm:</strong> Alex replaces snark with the Clean Signal.</li>
                    <li style={{marginBottom: 8}}><strong>No Defense:</strong> You replace counter-attack with the Meta-Response.</li>
                    <li style={{marginBottom: 8}}><strong>Daily Reset:</strong> {SCENE.upgrade.agreement.system}</li>
                    <li><strong>Appreciation:</strong> {SCENE.upgrade.agreement.appreciation}</li>
                  </ul>

                  <button 
                    onClick={reset}
                    style={{
                      width: "100%",
                      background: "#22C55E",
                      color: "#022C22",
                      border: "none",
                      padding: "12px",
                      borderRadius: 8,
                      fontWeight: 700,
                      cursor: "pointer"
                    }}
                  >
                    Commit & End Simulation
                  </button>
                </div>
              )}

            </div>
          </div>
        )}

      </div>

      {/* FOOTER / PROGRESS */}
      <div style={{ height: 4, background: "#334155", width: "100%" }}>
        <div
          style={{
            height: "100%",
            background: step === 5 ? accentColor : step === 4 ? "#22C55E" : step === 1 ? "#F59E0B" : "#7C3AED",
            width: `${(step / 5) * 100}%`,
            transition: "width 0.3s ease",
          }}
        />
      </div>
    </section>
  );
}
